import { useVersionCheck } from './useVersionCheck';
import { useSignalRVersionNotification } from './useSignalRVersionNotification';
import { useSignalR } from 'contexts/SignalRContext';

export interface HybridVersionCheckResult {
  hasNewVersion: boolean;
  currentVersion: string;
  newVersion: string;
  isMandatory: boolean;
  countdownSeconds: number;
  message: string;
  source: 'signalr' | 'polling' | 'none';
  reloadApp: () => void;
  clearUpdate: () => void;
  forceCheck: () => Promise<void>;
}

/**
 * Hybrid version checking that combines SignalR push with polling fallback
 * 
 * Strategy:
 * 1. PRIMARY: SignalR push notifications (instant, 0-2 seconds)
 * 2. FALLBACK: Polling every 15 minutes if SignalR disconnected, or 30 minutes if connected
 * 3. SAFETY: Check on visibility change (when user returns to tab)
 * 
 * This ensures 100% coverage with no blind spots:
 * - Connected clients: Instant updates via SignalR
 * - Disconnected clients: Polling fallback catches them
 * - Sleeping tabs: Visibility check when user returns
 * - Long sessions: Periodic polling as backup
 * 
 * @example
 * ```tsx
 * const { 
 *   hasNewVersion, 
 *   isMandatory, 
 *   countdownSeconds,
 *   source,
 *   reloadApp 
 * } = useHybridVersionCheck();
 * 
 * if (hasNewVersion) {
 *   console.log(`Update detected via ${source}`);
 *   
 *   if (isMandatory) {
 *     // Show countdown
 *     showMandatoryReload(countdownSeconds, reloadApp);
 *   } else {
 *     // Show optional notification
 *     showOptionalReload(reloadApp);
 *   }
 * }
 * ```
 */
export function useHybridVersionCheck(): HybridVersionCheckResult {
  const { status: signalRStatus } = useSignalR();
  const isSignalRConnected = signalRStatus === 'connected';

  // Check for debug interval override in sessionStorage
  const getCheckInterval = () => {
    try {
      const override = sessionStorage.getItem('versionCheckIntervalOverride');
      if (override) {
        const intervalSeconds = parseInt(override, 10);
        if (!isNaN(intervalSeconds) && intervalSeconds > 0) {
          console.log(`[HybridVersionCheck] Using debug interval override: ${intervalSeconds} seconds`);
          return intervalSeconds * 1000;
        }
      }
    } catch (error) {
      console.warn('[HybridVersionCheck] Failed to read interval override:', error);
    }
    
    // Default: Smart polling interval based on SignalR connection:
    // - If SignalR connected: Poll every 30 min (just as safety net)
    // - If SignalR disconnected: Poll every 5 min (primary detection method)
    return isSignalRConnected ? 30 * 60 * 1000 : 5 * 60 * 1000;
  };

  // SignalR push notifications (PRIMARY - instant updates)
  const { 
    hasUpdate: hasSignalRUpdate, 
    versionData: signalRData,
    clearUpdate: clearSignalRUpdate
  } = useSignalRVersionNotification();

  // Polling fallback (SECONDARY - safety net)
  const {
    hasNewVersion: hasPollingUpdate,
    currentVersion,
    newVersion: pollingVersion,
    reloadApp: pollingReload,
    checkNow: checkPollingNow
  } = useVersionCheck({
    checkInterval: getCheckInterval(),
    
    // Check immediately when user returns to tab
    checkOnVisibilityChange: true,
    
    // Callback for logging
    onNewVersion: (current, latest) => {
      console.log('[HybridVersionCheck] Polling detected new version:', {
        current,
        latest,
        signalRConnected: isSignalRConnected
      });
    }
  });

  // Determine which source detected the update
  let source: 'signalr' | 'polling' | 'none' = 'none';
  let hasNewVersion = false;
  let isMandatory = false;
  let countdownSeconds = 30;
  let message = 'A new version is available';
  let newVersion = currentVersion;

  if (hasSignalRUpdate && signalRData) {
    // SignalR update takes priority (instant)
    source = 'signalr';
    hasNewVersion = true;
    isMandatory = signalRData.isMandatory;
    countdownSeconds = signalRData.countdownSeconds;
    message = signalRData.message;
    newVersion = signalRData.version;
  } else if (hasPollingUpdate) {
    // Polling update as fallback
    // Check if user previously dismissed this version (in this session)
    const dismissedVersion = sessionStorage.getItem('dismissedVersion');
    
    if (dismissedVersion !== pollingVersion) {
      // Only show if not previously dismissed in this session
      source = 'polling';
      hasNewVersion = true;
      isMandatory = false;
      message = 'A new version is available';
      newVersion = pollingVersion;
    } else {
      console.log('[HybridVersionCheck] User previously dismissed polling version:', pollingVersion);
    }
  }

  const reloadApp = () => {
    console.log('[HybridVersionCheck] Reloading app, source:', source);
    
    // Clear dismissed version from sessionStorage before reloading
    try {
      sessionStorage.removeItem('dismissedVersion');
    } catch (error) {
      console.error('[HybridVersionCheck] Failed to clear dismissed version:', error);
    }
    
    pollingReload();
  };

  const clearUpdate = () => {
    clearSignalRUpdate();
    // Note: Polling updates clear themselves on reload
  };

  return {
    hasNewVersion,
    currentVersion,
    newVersion,
    isMandatory,
    countdownSeconds,
    message,
    source,
    reloadApp,
    clearUpdate,
    forceCheck: checkPollingNow
  };
}

